import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  ElementRef,
  ViewChildren,
  QueryList,
  AfterViewInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import startOfMonth from 'date-fns/startOfMonth';
import setDate from 'date-fns/setDate';
import { ForecastWeightsEnum } from '../../../models/enums';
import { ForecastHelperService } from '../../../services/forecast-helper.service';
import { ForecastSettingsData } from '../../../models/interfaces';

@Component({
  selector: 'app-day-weights',
  templateUrl: './day-weights.component.html',
  styleUrls: ['./day-weights.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DayWeightsComponent),
      multi: true,
    },
  ],
})
@DecorateUntilDestroy()
export class DayWeightsComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  @ViewChildren('cells') cellsRefQuery: QueryList<ElementRef>;
  @Input() data: ForecastSettingsData;
  @Output() dayWeightsEvent = new EventEmitter<number[]>();
  @Output() changeCustomIntervalEvent = new EventEmitter<string[]>();
  form: UntypedFormGroup;
  days: string[] = [];
  weekStartOffset = 0;
  weekEndOffset = 0;
  dayWeightsDataSource: number[][] = [];
  dayWeightsValid: Function;
  dayWeightsColumnsToDisplay: string[] = [];
  forecastWeights = ForecastWeightsEnum;
  private customDays: string[] = [];

  constructor(private formBuilder: UntypedFormBuilder, private forecastHelperService: ForecastHelperService) {}

  ngOnInit(): void {
    this.dayWeightsValid = this.forecastHelperService.getDayWeights;
    this.form = this.formBuilder.group({
      [ForecastWeightsEnum.dayWeightsGroup]: this.getDayWeightsControls(),
    });
    this.form
      .get(ForecastWeightsEnum.dayWeightsGroup)
      ?.valueChanges.pipe(debounceTime(100), takeUntilDestroyed(this))
      .subscribe(item => {
        this.onChange(item);
        this.writeValue(item);
      });

    this.customDays = Object.keys(this.data.customTimeWeights);
  }

  ngAfterViewInit(): void {
    if (this.weekEndOffset > 28) {
      const itemRefQueryArray = this.cellsRefQuery.toArray();
      for (let dayIndex = 1 + (7 - (35 - this.weekEndOffset)); dayIndex <= 7; dayIndex++) {
        // eslint-disable-next-line functional/immutable-data
        itemRefQueryArray[dayIndex * 5 - 1].nativeElement.style.border = 0;
      }
    }
    const dayWeightsGroupValue = this.form.get(ForecastWeightsEnum.dayWeightsGroup)?.value;
    this.onChange(dayWeightsGroupValue);
    this.writeValue(dayWeightsGroupValue);
  }

  isInvalid(formControlName: number): boolean {
    const forecastWeightsControls = (this.form.get(ForecastWeightsEnum.dayWeightsGroup) as UntypedFormGroup)?.controls;
    if (forecastWeightsControls && forecastWeightsControls[formControlName]) {
      return forecastWeightsControls[formControlName].invalid;
    } else {
      return false;
    }
  }

  isCustomEnabled(day: string): boolean {
    return Object.keys(this.data.customTimeWeights).includes(day);
  }

  changeCustomInterval(day: string, value: boolean): void {
    if (value) {
      this.customDays.push(day);
    } else {
      this.customDays = this.customDays.filter(value => value !== day);
    }

    this.changeCustomIntervalEvent.emit(this.customDays);
  }

  private getDayWeightsControls(): UntypedFormArray {
    const monthStart = startOfMonth(this.data.date);
    this.dayWeightsColumnsToDisplay = eachDayOfInterval({ start: monthStart, end: setDate(this.data.date, 7) }).map(
      item => String(item),
    );
    const dayWeightsControls: unknown[] = [];
    this.weekEndOffset = getDaysInMonth(this.data.date);
    for (let dayIndex = 0; dayIndex < getDaysInMonth(this.data.date); dayIndex++) {
      this.days.push(format(addDays(monthStart, dayIndex), 'd'));
      // eslint-disable-next-line functional/immutable-data
      dayWeightsControls.push([this.data.dayWeights[dayIndex] || 0, [Validators.required, Validators.min(0)]]);
    }
    this.dayWeightsDataSource = new Array(Math.ceil(getDaysInMonth(this.data.date) / 7)).fill([0, 1, 2, 3, 4, 5, 6]);
    return this.formBuilder.array(dayWeightsControls);
  }

  uploadDayWeights(dayWeights: number[][]): void {
    (this.form.get(ForecastWeightsEnum.dayWeightsGroup) as UntypedFormArray).controls.forEach(
      (item: AbstractControl, index: number) => {
        item.setValue(dayWeights[0][index]);
      },
    );
  }

  writeValue(dayWeights: number[]): void {
    this.dayWeightsEvent.emit(dayWeights);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-function-return-type
  onChange: (value: any) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
